import { BLOCK_ALIGN_TYPES } from "@corca/tiptap";
import { findParentNodeClosestToPos } from "@tiptap/core";
import { Selection } from "@tiptap/pm/state";
import {
  addColumn,
  addColumnAfter,
  addRow,
  addRowAfter,
  columnIsHeader,
  isInTable,
  nextCell,
  removeColumn,
  removeRow,
  selectedRect,
  TableMap,
} from "@tiptap/pm/tables";

import {
  getColumnCells,
  getHeaderCells,
  getNearestCellCoords,
  isCellLike,
  updateRect,
} from "@/components/tiptap/tables/utils";

import type { Alignment } from "@/components/doc/right-sidebar/align-dropdown/AlignDropdown";
import type { CellAttrs } from "@/components/tiptap/tables/types";
import type { TailwindAttributes } from "@corca/tiptap";
import type { CommandProps } from "@tiptap/core";
import type { Attrs, Node as ProseMirrorNode } from "@tiptap/pm/model";
import type { EditorState, Transaction } from "@tiptap/pm/state";
import type { TableRect } from "@tiptap/pm/tables";

export type HeaderType = "row" | "column";

export function copyCellAttrs(
  tr: Transaction,
  rect: TableRect,
  { from, to, keys = [] }: { from: number; to: number; keys?: string[] },
): Transaction {
  const destinationPos = rect.map.map[to];
  const sourceCell = rect.table.nodeAt(rect.map.map[from]);
  const destinationCell = rect.table.nodeAt(destinationPos);

  if (!sourceCell || !destinationCell) {
    return tr;
  }

  let { attrs } = sourceCell;

  if (keys.length > 0) {
    attrs = keys.reduce<Attrs>((acc, key) => {
      if (sourceCell.attrs[key]) {
        return {
          ...acc,
          [key]: sourceCell.attrs[key] as unknown,
        };
      }

      return acc;
    }, {});
  }

  return tr.setNodeMarkup(destinationPos + rect.tableStart, undefined, {
    ...destinationCell.attrs,
    ...attrs,
  });
}

export const appendRow =
  (rows: number = 1) =>
  ({ dispatch, state }: CommandProps): boolean => {
    if (!isInTable(state)) {
      return false;
    }

    if (dispatch) {
      const rect = selectedRect(state);
      let { tr } = state;

      for (let i = 0; i < rows; i++) {
        tr = addRow(tr, rect, rect.map.height);
      }

      const headerCellIndex = (rect.map.height - 1) * rect.map.width;
      const node = rect.table.nodeAt(rect.map.map[headerCellIndex]);

      if (node?.type.name === "tableHeader") {
        const newRect = selectedRect(state.apply(tr));
        const isHeader = newRect.map.height > 1 && columnIsHeader(newRect.map, newRect.table, 0);

        if (isHeader) {
          for (let i = 0; i < rows; i++) {
            const targetRowIndex = newRect.map.height - rows + i;
            const targetHeaderCellIndex = targetRowIndex * newRect.map.width;

            tr = copyCellAttrs(tr, newRect, {
              from: headerCellIndex,
              to: targetHeaderCellIndex,
              keys: ["backgroundColor", "alignment", "size", "color"],
            });
          }
        }
      }

      if (tr.docChanged) {
        dispatch(tr);
        return true;
      }

      return false;
    }

    return false;
  };

export const appendColumn =
  (cols: number = 1) =>
  ({ dispatch, state }: CommandProps): boolean => {
    if (!isInTable(state)) {
      return false;
    }

    if (dispatch) {
      const rect = selectedRect(state);
      let { tr } = state;

      for (let i = 0; i < cols; i++) {
        tr = addColumn(tr, rect, rect.map.width);
      }

      const headerIndex = rect.map.width - 1;
      const node = rect.table.nodeAt(rect.map.map[headerIndex]);

      if (node?.type.name === "tableHeader") {
        const newRect = selectedRect(state.apply(tr));

        for (let i = 0; i < cols; i++) {
          tr = copyCellAttrs(tr, newRect, {
            from: headerIndex,
            to: headerIndex + i + 1,
            keys: ["backgroundColor", "alignment", "size", "color"],
          });
        }
      }

      if (tr.docChanged) {
        dispatch(tr);
        return true;
      }

      return false;
    }

    return false;
  };

export const popRow =
  (rows: number = 1) =>
  ({ state, dispatch }: CommandProps): boolean => {
    if (!isInTable(state) || !dispatch) {
      return false;
    }

    let { tr } = state;
    let rect: TableRect | null = selectedRect(state);
    const { col, row } = getNearestCellCoords(rect, rows);

    for (let i = 0; i < rows; i++) {
      removeRow(tr, rect, rect.map.height - 1);
      rect = updateRect(rect, tr);

      if (!rect) {
        return false;
      }
    }

    if (rect.bottom > rect.map.height) {
      const cell = rect.map.positionAt(row, col, rect.table);
      const cellPos = tr.doc.resolve(cell + rect.tableStart);

      tr = tr.setSelection(Selection.near(cellPos));
    }

    if (tr.docChanged) {
      dispatch(tr);
      return true;
    }

    return false;
  };

export const popColumn =
  (cols: number = 1) =>
  ({ state, dispatch, chain }: CommandProps): boolean => {
    if (!isInTable(state) || !dispatch) {
      return false;
    }

    let rect: TableRect | null = selectedRect(state);
    const { col, row } = getNearestCellCoords(rect, 0, cols);

    if (cols === 1) {
      let { tr } = state;

      removeColumn(tr, rect, rect.map.width - 1);
      rect = updateRect(rect, tr);

      if (!rect) {
        return false;
      }

      if (rect.right > rect.map.width) {
        const cell = rect.map.positionAt(row, col, rect.table);
        const cellPos = tr.doc.resolve(cell + rect.tableStart);

        tr = tr.setSelection(Selection.near(cellPos));
      }

      if (tr.docChanged) {
        dispatch(tr);
        return true;
      }

      return false;
    }

    const anchorPos = rect.map.positionAt(0, rect.map.width - cols, rect.table);
    const headPos = rect.map.positionAt(rect.map.height - 1, rect.map.width - 1, rect.table);

    return chain()
      .setCellSelection({
        anchorCell: anchorPos + rect.tableStart,
        headCell: headPos + rect.tableStart,
      })
      .deleteColumn()
      .command(({ state: newState, dispatch: newDispatch }) => {
        let { tr } = newState;
        const newRect = selectedRect(newState);

        const cell = newRect.map.positionAt(row, col, newRect.table);
        const cellPos = tr.doc.resolve(cell + newRect.tableStart);

        tr = tr.setSelection(Selection.near(cellPos));

        if (newDispatch && tr.docChanged) {
          newDispatch(tr);
          return true;
        }

        return false;
      })
      .run();
  };

export const addColumnAfterWithHeaderAttrs =
  (keys?: string[]) =>
  ({ state, chain, dispatch }: CommandProps): boolean => {
    if (!isInTable(state)) {
      return false;
    }

    const rect = selectedRect(state);
    const node = rect.table.nodeAt(rect.map.map[rect.left]);

    if (node?.type.name !== "tableHeader") {
      return addColumnAfter(state, dispatch);
    }

    return chain()
      .command((props) => addColumnAfter(props.state, props.dispatch))
      .command((props) => {
        let { tr } = props.state;

        if (props.dispatch) {
          const newRect = selectedRect(props.state);
          const params = {
            from: newRect.left,
            to: newRect.left + 1,
            keys,
          };

          tr = copyCellAttrs(tr, newRect, params);

          if (tr.docChanged) {
            props.dispatch(tr);
          }
        }

        return true;
      })
      .run();
  };

export const addRowAfterWithHeaderAttrs =
  (keys?: string[]) =>
  ({ state, chain, dispatch }: CommandProps): boolean => {
    if (!isInTable(state)) {
      return false;
    }

    const rect = selectedRect(state);
    const pos = rect.top * rect.map.width;
    const node = rect.table.nodeAt(rect.map.map[pos]);
    const isHeader = rect.map.height > 1 && columnIsHeader(rect.map, rect.table, 0);

    if (node?.type.name !== "tableHeader" || !isHeader) {
      return addRowAfter(state, dispatch);
    }

    return chain()
      .command((props) => addRowAfter(props.state, props.dispatch))
      .command((props) => {
        let { tr } = props.state;

        if (props.dispatch) {
          const newRect = selectedRect(props.state);
          const params = {
            from: newRect.top * newRect.map.width,
            to: (newRect.top + 1) * newRect.map.width,
            keys,
          };

          tr = copyCellAttrs(tr, newRect, params);

          if (tr.docChanged) {
            props.dispatch(tr);
          }
        }

        return true;
      })
      .run();
  };

export const goToNextCellVertical =
  (direction: number) =>
  ({ state, dispatch }: CommandProps): boolean => {
    if (!isInTable(state)) {
      return false;
    }

    const { $head } = state.selection;
    const currentCell = findParentNodeClosestToPos(
      $head,
      (node) => node.type.name === "tableCell" || node.type.name === "tableHeader",
    );

    if (currentCell) {
      const cellPos = state.doc.resolve(currentCell.pos);
      const next = nextCell(cellPos, "vert", direction);

      if (!next) {
        return false;
      }

      const newSel = Selection.near(next, 1);

      if (dispatch) {
        dispatch(state.tr.setSelection(newSel).scrollIntoView());
        return true;
      }
    }

    return false;
  };

function applyCellTextStyle(
  tr: Transaction,
  state: EditorState,
  tableStart: number,
  cellPos: number,
  cellNode: ProseMirrorNode,
  textStyle: TailwindAttributes,
): void {
  const start = tableStart + cellPos;

  tr.setNodeMarkup(start, cellNode.type, {
    ...cellNode.attrs,
    ...textStyle,
  });

  cellNode.descendants((childNode, childPos) => {
    const marks = childNode.marks.filter((mark) => mark.type.name === "textStyle");
    const markStart = start + childPos + 1;
    const markEnd = markStart + childNode.nodeSize;

    if (marks.length > 0) {
      marks.forEach((mark) => {
        const attrs = { ...mark.attrs, ...textStyle };

        tr.removeMark(markStart, markEnd, mark.type);
        tr.addMark(markStart, markEnd, mark.type.create(attrs));
      });
    } else {
      const mark = state.schema.mark("textStyle", textStyle);
      tr.addMark(markStart, markEnd, mark);
    }

    return true;
  });
}

export const setHeaderTextStyle =
  (textStyle: TailwindAttributes, type: HeaderType = "row") =>
  ({ state, dispatch }: CommandProps): boolean => {
    if (!isInTable(state) || !dispatch) {
      return false;
    }

    const rect = selectedRect(state);
    const { tr } = state;
    const cells = getHeaderCells(rect.table, type);

    cells.forEach(({ node, pos }) => {
      applyCellTextStyle(tr, state, rect.tableStart, pos, node, textStyle);
    });

    if (tr.docChanged) {
      dispatch(tr);
      return true;
    }

    return false;
  };

export const setTableCellTextStyle =
  (textStyle: TailwindAttributes) =>
  ({ state, dispatch }: CommandProps): boolean => {
    if (!isInTable(state) || !dispatch) {
      return false;
    }

    const rect = selectedRect(state);
    const { tr } = state;
    const seen = new Set<number>();

    for (let row = rect.top; row < rect.bottom; row++) {
      for (let col = rect.left; col < rect.right; col++) {
        const pos = rect.map.map[row * rect.map.width + col];

        if (seen.has(pos)) {
          continue;
        }
        seen.add(pos);

        const node = rect.table.nodeAt(pos);

        if (!node || !isCellLike(node)) {
          continue;
        }

        applyCellTextStyle(tr, state, rect.tableStart, pos, node, textStyle);
      }
    }

    const textStyleMark = state.schema.marks.textStyle;
    if (textStyleMark) {
      const existing = state.storedMarks ?? state.selection.$from.marks();
      const filtered = existing.filter((mark) => mark.type !== textStyleMark);
      const previous = existing.find((mark) => mark.type === textStyleMark);
      const mergedAttrs = { ...(previous?.attrs ?? {}), ...textStyle };
      tr.setStoredMarks([...filtered, textStyleMark.create(mergedAttrs)]);
    }

    if (tr.docChanged) {
      dispatch(tr);
      return true;
    }

    return false;
  };

export const setHeaderAttrs =
  (attrs: Attrs, type: HeaderType = "row") =>
  ({ state, dispatch }: CommandProps): boolean => {
    if (!isInTable(state) || !dispatch) {
      return false;
    }

    const { tr } = state;
    const rect = selectedRect(state);
    const cells = getHeaderCells(rect.table, type);

    cells.forEach(({ node, pos }) => {
      const start = rect.tableStart + pos;

      tr.setNodeMarkup(start, node.type, {
        ...node.attrs,
        ...attrs,
      });
    });

    if (tr.docChanged) {
      dispatch(tr);
      return true;
    }

    return false;
  };

export const setHeaderTextAlignment =
  (alignment: Alignment, type: HeaderType = "row") =>
  ({ state, dispatch }: CommandProps): boolean => {
    if (!isInTable(state) || !dispatch) {
      return false;
    }

    const rect = selectedRect(state);
    const { tr } = state;
    const cells = getHeaderCells(rect.table, type);

    cells.forEach(({ node, pos }) => {
      const start = rect.tableStart + pos;

      tr.setNodeMarkup(start, node.type, {
        ...node.attrs,
        alignment,
      });

      node.descendants((childNode, childPos) => {
        if (BLOCK_ALIGN_TYPES.includes(childNode.type.name)) {
          tr.setNodeMarkup(start + childPos + 1, childNode.type, {
            ...childNode.attrs,
            alignment,
          });
        }
      });
    });

    if (tr.docChanged) {
      dispatch(tr);
      return true;
    }

    return false;
  };

export const setColumnWidth =
  (cellPos: number, width: number | null) =>
  ({ tr, state, dispatch }: CommandProps): boolean => {
    if (!dispatch) {
      return false;
    }

    const $cell = state.doc.resolve(cellPos);
    const table = $cell.node(-1);
    const map = TableMap.get(table);
    const tableStart = $cell.start(-1);

    const colIndex = map.colCount(cellPos - tableStart);
    const cells = getColumnCells(table, colIndex);

    cells.forEach(({ node, pos }) => {
      const attrs = node.attrs as CellAttrs;

      /**
       * For now we only check the first item in `colwidth` because we don't support merge and split yet.
       */
      if (!attrs.colwidth || attrs.colwidth[0] !== width) {
        const colwidth = attrs.colwidth ? attrs.colwidth.slice() : Array(attrs.colspan).fill(0);

        colwidth[0] = width;

        tr.setNodeMarkup(tableStart + pos, node.type, {
          ...node.attrs,
          colwidth,
        });
      }
    });

    if (tr.docChanged) {
      dispatch(tr);
      return true;
    }

    return false;
  };

export const setRowHeight =
  (rowPos: number, height: number | null) =>
  ({ tr, state, dispatch }: CommandProps): boolean => {
    if (!dispatch) {
      return false;
    }

    const row = state.doc.nodeAt(rowPos);

    if (!row || row.type.name !== "tableRow") {
      return false;
    }

    if (row.attrs.height !== height) {
      tr.setNodeMarkup(rowPos, row.type, {
        ...row.attrs,
        height,
      });
    }

    if (tr.docChanged) {
      dispatch(tr);
      return true;
    }

    return false;
  };
